import { ButtonHTMLAttributes, ForwardedRef, ReactNode } from 'react';

import { Styles, css, cx } from '@flipster/styled-system/css';
import { textButton, type TextButtonVariantProps } from '@flipster/styled-system/recipes';

import { IsomorphicLink, IsomorphicLinkProps } from '../design-utils/isomorphic-link';

interface BaseProps {
  // variants
  color?: TextButtonVariantProps['color'];
  size?: TextButtonVariantProps['size'];
  // slots
  label?: ReactNode;
  icon?: ReactNode;
  css?: Styles;
}

type ButtonElementProps = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps | 'className' | 'children'> & {
    ref?: ForwardedRef<HTMLButtonElement>;
  };
type LinkElementProps = BaseProps &
  Omit<IsomorphicLinkProps, keyof BaseProps | 'className' | 'children'> & {
    ref?: ForwardedRef<HTMLAnchorElement>;
  };
export type TextButtonProps<T extends 'button' | 'a' | undefined = 'button'> = { as?: T } & (T extends 'button'
  ? ButtonElementProps
  : LinkElementProps);

const ButtonComponent = ({ ref, label, icon, color, size, css: cssProps, ...htmlProps }: ButtonElementProps) => {
  const classes = textButton({ color, size });

  return (
    <button type='button' className={cx(classes.root, css(cssProps))} ref={ref} {...htmlProps}>
      {label && <div className={classes.label}>{label}</div>}
      {icon && <div className={classes.icon}>{icon}</div>}
    </button>
  );
};

const LinkComponent = ({ ref, label, icon, color, size, css: cssProps, ...htmlProps }: LinkElementProps) => {
  const classes = textButton({ color, size });

  return (
    <IsomorphicLink className={cx(classes.root, css(cssProps))} ref={ref} {...htmlProps}>
      {label && <div className={classes.label}>{label}</div>}
      {icon && <div className={classes.icon}>{icon}</div>}
    </IsomorphicLink>
  );
};

export const TextButton = <T extends 'button' | 'a' | undefined = 'button'>({
  as = 'button',
  ...htmlProps
}: TextButtonProps<T>) => {
  if (as === 'button') return <ButtonComponent {...(htmlProps as ButtonElementProps)} />;
  if (as === 'a') return <LinkComponent {...(htmlProps as LinkElementProps)} />;
  return null;
};
